import { useCallback, useEffect, useMemo, useState } from 'react';
import { useResourcesConfig } from '../config/ResourcesConfigContext';

const RESOLVE_PATH = '/content/files/resolve';

// The backend caps a resolve batch at 50 ids.
const MAX_BATCH = 50;

interface ResolveResponse {
    urls: Record<string, string>;
    /** fileId -> the name the file was uploaded with. Optional: older backends omit it. */
    names?: Record<string, string>;
    expiresAt: string;
}

interface ResolvedContentFiles {
    urls: Record<string, string>;
    names: Record<string, string>;
}

interface Envelope<T> {
    success?: boolean;
    data?: T;
}

/** Unwraps a v2 payload whether or not the host's interceptor already stripped the envelope. */
function unwrap<T>(responseData: T | Envelope<T>): T | null {
    if (responseData && typeof responseData === 'object' && 'success' in responseData) {
        return (responseData as Envelope<T>).data ?? null;
    }
    return (responseData as T) ?? null;
}

/** Exchanges content fileIds for signed URLs + original names, batched. */
function useResolveContentFiles(): (fileIds: string[]) => Promise<ResolvedContentFiles> {
    const { api } = useResourcesConfig();
    return useCallback(
        async (fileIds: string[]): Promise<ResolvedContentFiles> => {
            const resolved: ResolvedContentFiles = { urls: {}, names: {} };
            if (fileIds.length === 0) return resolved;

            for (let i = 0; i < fileIds.length; i += MAX_BATCH) {
                const batch = fileIds.slice(i, i + MAX_BATCH);
                const response = await api.post<ResolveResponse | Envelope<ResolveResponse>>(
                    RESOLVE_PATH,
                    { fileIds: batch }
                );
                const payload = unwrap<ResolveResponse>(response.data);
                Object.assign(resolved.urls, payload?.urls ?? {});
                Object.assign(resolved.names, payload?.names ?? {});
            }
            return resolved;
        },
        [api]
    );
}

interface ContentFileUrlsState {
    urls: Record<string, string>;
    /** fileId -> original upload filename, when the backend provides it. */
    names: Record<string, string>;
    isLoading: boolean;
    error: string | null;
}

/** Resolves a stable set of fileIds into state; idle on null/[]. URLs are
 * short-lived — re-resolve, don't cache elsewhere. */
export function useContentFileUrls(fileIds: string[] | null): ContentFileUrlsState {
    const resolve = useResolveContentFiles();
    // Start in loading when there is work: an initial false flashes "not available" for a frame.
    const [state, setState] = useState<ContentFileUrlsState>({
        urls: {},
        names: {},
        isLoading: (fileIds?.length ?? 0) > 0,
        error: null
    });

    // Key on the ids themselves so a new array with the same contents does not
    // retrigger the request on every render.
    const key = useMemo(() => (fileIds ?? []).join(','), [fileIds]);

    useEffect(() => {
        const ids = key.length > 0 ? key.split(',') : [];
        if (ids.length === 0) {
            setState({ urls: {}, names: {}, isLoading: false, error: null });
            return;
        }

        let cancelled = false;
        setState({ urls: {}, names: {}, isLoading: true, error: null });

        resolve(ids)
            .then(({ urls, names }) => {
                if (!cancelled) setState({ urls, names, isLoading: false, error: null });
            })
            .catch(() => {
                if (!cancelled) {
                    setState({
                        urls: {},
                        names: {},
                        isLoading: false,
                        error: 'Unable to load this file. Please try again later.'
                    });
                }
            });

        return () => {
            cancelled = true;
        };
    }, [key, resolve]);

    return state;
}

/** Single-file convenience wrapper over {@link useContentFileUrls}. */
export function useContentFileUrl(fileId: string | null | undefined): {
    url: string | null;
    /** The name the file was uploaded with, when the backend provides it. */
    name: string | null;
    isLoading: boolean;
    error: string | null;
} {
    const ids = useMemo(() => (fileId ? [fileId] : null), [fileId]);
    const { urls, names, isLoading, error } = useContentFileUrls(ids);
    return {
        url: fileId ? (urls[fileId] ?? null) : null,
        name: fileId ? (names[fileId] ?? null) : null,
        isLoading,
        error
    };
}
